import { useEffect, useState } from 'react';

import { App, Button, Input, Modal, Typography } from 'antd';

import { formatDate } from 'helpers/date';
import { useImpactCertificate } from 'v2/hooks/useImpactCertificate';
import type { ImpactReportView } from 'v2/interfaces/impactMeter';

import { IMPACT_DIPLOMA_NAME_MAX_LENGTH } from './constants';
import { formatIntegerImpact } from './formatImpact';

type DiplomaModalProps = {
  open: boolean;
  onClose: () => void;
  userId: number;
  defaultName: string;
  report: ImpactReportView;
};

function getSafeCertificateUrl(value?: string) {
  if (!value) {
    return null;
  }

  try {
    const url = new URL(value);
    const isHttps = url.protocol === 'https:';
    const isSameOrigin = url.origin === window.location.origin;

    return isHttps || isSameOrigin ? url.toString() : null;
  } catch {
    return null;
  }
}

const CERTIFICATE_TAB_PATH = '/impact-diploma-loading.html';
const CERTIFICATE_TAB_READY_TIMEOUT_MS = 3000;
const CERTIFICATE_TAB_POLL_MS = 50;

function openCertificateTab() {
  return window.open(CERTIFICATE_TAB_PATH, '_blank');
}

function waitForCertificateTab(popup: Window) {
  return new Promise<void>(resolve => {
    const startedAt = Date.now();

    const timer = window.setInterval(() => {
      const timedOut = Date.now() - startedAt >= CERTIFICATE_TAB_READY_TIMEOUT_MS;

      if (popup.closed || timedOut) {
        window.clearInterval(timer);
        resolve();
        return;
      }

      try {
        if (popup.document.body?.dataset.ready === 'true') {
          window.clearInterval(timer);
          resolve();
        }
      } catch {
        window.clearInterval(timer);
        resolve();
      }
    }, CERTIFICATE_TAB_POLL_MS);
  });
}

function DiplomaModal({ open, onClose, userId, defaultName, report }: DiplomaModalProps) {
  const { message } = App.useApp();
  const { mutateAsync, isPending } = useImpactCertificate();
  const [name, setName] = useState(defaultName.slice(0, IMPACT_DIPLOMA_NAME_MAX_LENGTH));
  const formattedDate = formatDate(report.updatedAt);

  useEffect(() => {
    if (open) {
      setName(defaultName.slice(0, IMPACT_DIPLOMA_NAME_MAX_LENGTH));
    }
  }, [defaultName, open]);

  const handleDownload = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      message.warning('Informe o nome que deve aparecer no diploma.');
      return;
    }

    const popup = openCertificateTab();

    if (!popup) {
      message.warning('Permita pop-ups para abrir o diploma em uma nova aba.');
      return;
    }

    try {
      const certificate = await mutateAsync({ userId, name: trimmedName });
      const certificateUrl = getSafeCertificateUrl(certificate?.url);

      if (!certificateUrl) {
        popup.close();
        message.error('O endereço do diploma retornado é inválido.');
        return;
      }

      await waitForCertificateTab(popup);

      if (popup.closed) {
        return;
      }

      popup.location.replace(certificateUrl);
      onClose();
    } catch {
      popup.close();
      message.error('Não foi possível gerar o diploma. Tente novamente.');
    }
  };

  return (
    <Modal
      wrapProps={{ id: 'impact-meter-diploma' }}
      open={open}
      onCancel={onClose}
      title='Seu diploma de Criador (a) de Impacto'
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type='primary' loading={isPending} disabled={isPending} onClick={handleDownload}>
            Baixar diploma
          </Button>
        </>
      }
    >
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-1'>
          <Typography.Text id='impact-meter-diploma-name-label'>
            Como você quer que seu nome apareça no diploma?
          </Typography.Text>
          <Input
            id='impact-meter-diploma-name'
            aria-labelledby='impact-meter-diploma-name-label'
            aria-describedby='impact-meter-diploma-hint'
            maxLength={IMPACT_DIPLOMA_NAME_MAX_LENGTH}
            value={name}
            onChange={event => setName(event.target.value)}
          />
          <Typography.Text id='impact-meter-diploma-hint' type='secondary'>
            O nome é só para exibição. O número de impacto não pode ser editado.
          </Typography.Text>
        </div>

        <div className='flex flex-col items-center justify-center rounded-(--uizz-antd-border-radius-lg) bg-[#0F1324] px-6 py-12 text-center text-(--uizz-antd-color-white)'>
          <div className='mb-6 size-10 rounded-full border border-solid border-(--uizz-antd-color-warning)' />
          <Typography.Text className='text-[11px] tracking-[2px] text-(--uizz-antd-color-white) uppercase opacity-80'>
            Diploma de Criador (a) de Impacto
          </Typography.Text>
          <Typography.Title level={2} className='mt-3 mb-2 text-(--uizz-antd-color-white)'>
            {name.trim() || defaultName}
          </Typography.Title>
          <div className='mb-4 h-px w-24 bg-(--uizz-antd-color-warning)' />
          <Typography.Text className='text-(--uizz-antd-color-white)'>
            impactou{' '}
            <Typography.Text strong className='text-(--uizz-antd-color-white)'>
              {formatIntegerImpact(report.estimatedImpactCount)}
            </Typography.Text>{' '}
            pessoas com a Eduzz
          </Typography.Text>
          {formattedDate && (
            <Typography.Text className='mt-2 text-xs text-(--uizz-antd-color-white) opacity-70'>
              Dados atualizados até {formattedDate}
            </Typography.Text>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default DiplomaModal;
